import React, { useEffect, useState } from 'react';
import CrudTable from '../../components/common/CrudTable';
import { enrolmentAPI, studentAPI, groupAPI } from '../../api/services';
import { FiX } from 'react-icons/fi';

const EnrolmentsAdmin = () => {
  const [students, setStudents] = useState([]);
  const [groups, setGroups] = useState([]);
  const [showQuickAddStudent, setShowQuickAddStudent] = useState(false);
  const [showQuickAddGroup, setShowQuickAddGroup] = useState(false);
  const [quickStudentForm, setQuickStudentForm] = useState({ fullName: '', studentPhone: '' });
  const [quickGroupForm, setQuickGroupForm] = useState({ name: '', description: '' });
  const [savingStudent, setSavingStudent] = useState(false);
  const [savingGroup, setSavingGroup] = useState(false);

  const loadStudents = () => {
    studentAPI.getAll()
      .then(res => setStudents(res.data?.data || []))
      .catch(() => {});
  };

  const loadGroups = () => {
    groupAPI.getAll()
      .then(res => setGroups(res.data?.data || []))
      .catch(() => {});
  };

  useEffect(() => {
    loadStudents();
    loadGroups();
  }, []);

  const handleQuickAddStudent = async (e) => {
    e.preventDefault();
    setSavingStudent(true);
    try {
      await studentAPI.add(quickStudentForm);
      loadStudents();
      setShowQuickAddStudent(false);
      setQuickStudentForm({ fullName: '', studentPhone: '' });
    } catch (err) {
      alert('Failed to add student');
    } finally {
      setSavingStudent(false);
    }
  };

  const handleQuickAddGroup = async (e) => {
    e.preventDefault();
    setSavingGroup(true);
    try {
      await groupAPI.add(quickGroupForm);
      loadGroups();
      setShowQuickAddGroup(false);
      setQuickGroupForm({ name: '', description: '' });
    } catch (err) {
      alert('Failed to add group');
    } finally {
      setSavingGroup(false);
    }
  };

  const COLUMNS = [
    {
      key: 'studentDto',
      label: 'Student',
      render: (item) => item.studentDto?.fullName || 'No Student',
    },
    {
      key: 'groupDto',
      label: 'Group',
      render: (item) => item.groupDto?.name || 'No Group',
    },
    {
      key: 'startedDate',
      label: 'Started Date',
      render: (item) => item.startedDate || '—',
    },
  ];

  const FORM_FIELDS = [
    {
      name: 'studentId',
      label: 'Student',
      type: 'select',
      options: students.map(s => ({ label: s.fullName, value: s.id })),
      onQuickAdd: () => setShowQuickAddStudent(true),
    },
    {
      name: 'groupId',
      label: 'Group',
      type: 'select',
      options: groups.map(g => ({ label: g.name, value: g.id })),
      onQuickAdd: () => setShowQuickAddGroup(true),
    },
    {
      name: 'startedDate',
      label: 'Started Date',
      type: 'date',
      placeholder: '2026-01-15',
    },
  ];

  return (
    <>
      <CrudTable
        title="Enrolments"
        entityName="Enrolment"
        columns={COLUMNS}
        formFields={FORM_FIELDS}
        fetchData={async () => {
          const res = await enrolmentAPI.getAll();
          return res.data?.data || [];
        }}
        onAdd={(data) => enrolmentAPI.add(data)}
        onUpdate={(id, data) => enrolmentAPI.update(id, data)}
        onDelete={(id) => enrolmentAPI.delete(id)}
      />

      {/* Quick Add Student Modal */}
      {showQuickAddStudent && (
        <div style={overlayStyle} onClick={() => setShowQuickAddStudent(false)}>
          <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h3 style={{ fontSize: '1.1rem', fontWeight: 700 }}>Quick Add Student</h3>
              <button onClick={() => setShowQuickAddStudent(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                <FiX />
              </button>
            </div>
            <form onSubmit={handleQuickAddStudent} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div className="form-group">
                <label>Full Name</label>
                <input
                  className="form-input"
                  required
                  value={quickStudentForm.fullName}
                  onChange={(e) => setQuickStudentForm({ ...quickStudentForm, fullName: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Phone</label>
                <input
                  className="form-input"
                  required
                  value={quickStudentForm.studentPhone}
                  onChange={(e) => setQuickStudentForm({ ...quickStudentForm, studentPhone: e.target.value })}
                />
              </div>
              <button type="submit" className="btn btn-primary" disabled={savingStudent}>
                {savingStudent ? 'Saving...' : 'Add Student'}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Quick Add Group Modal */}
      {showQuickAddGroup && (
        <div style={overlayStyle} onClick={() => setShowQuickAddGroup(false)}>
          <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h3 style={{ fontSize: '1.1rem', fontWeight: 700 }}>Quick Add Group</h3>
              <button onClick={() => setShowQuickAddGroup(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                <FiX />
              </button>
            </div>
            <form onSubmit={handleQuickAddGroup} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div className="form-group">
                <label>Group Name</label>
                <input
                  className="form-input"
                  required
                  value={quickGroupForm.name}
                  onChange={(e) => setQuickGroupForm({ ...quickGroupForm, name: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea
                  className="form-input"
                  rows={3}
                  value={quickGroupForm.description}
                  onChange={(e) => setQuickGroupForm({ ...quickGroupForm, description: e.target.value })}
                />
              </div>
              <button type="submit" className="btn btn-primary" disabled={savingGroup}>
                {savingGroup ? 'Saving...' : 'Add Group'}
              </button>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

const overlayStyle = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.5)',
  backdropFilter: 'blur(4px)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 3000,
};

const modalStyle = {
  background: '#fff',
  padding: 24,
  borderRadius: 16,
  width: '100%',
  maxWidth: 320,
};

export default EnrolmentsAdmin;
